import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { getRelativeTime } from '@pickleballinc/ui/components/global/utils/getRelativeTime';
import clsx from 'clsx';

import styles from '@/modules/homepage/NewsCard/NewsCard.module.css';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

export interface NewsCardProps {
  description?: string;
  externalNews?: boolean;
  image?: string;
  imageAltText?: string;
  publishDateDisplayed?: string | Date;
  title: string;
  author?: string;
  url?: string;
}

export const NewsCard = ({
  description,
  image,
  imageAltText,
  publishDateDisplayed,
  title,
  author,
  url,
}: NewsCardProps) => {
  const wrapperClasses = clsx(styles.wrapper);
  const imageClasses = clsx(styles.image);
  const contentClasses = clsx(styles.content);
  const titleClasses = clsx(styles.title);
  const descriptionClasses = clsx(styles.description);
  const iconTimeClasses = clsx(styles.icon__time);
  const iconClasses = clsx(styles.icon);
  const timeClasses = clsx(styles.time);

  const formattedPublishDate =
    publishDateDisplayed instanceof Date
      ? publishDateDisplayed
      : publishDateDisplayed
        ? new Date(publishDateDisplayed)
        : null;

  const relativeTime = formattedPublishDate ? getRelativeTime(formattedPublishDate) : '';

  return (
    <div className={wrapperClasses} itemScope itemType='https://schema.org/NewsArticle'>
      {url ? <meta itemProp='url' content={url} /> : null}
      {formattedPublishDate ? (
        <meta itemProp='datePublished' content={formattedPublishDate.toISOString()} />
      ) : null}
      <img
        className={imageClasses}
        style={{ objectFit: 'cover' }}
        alt={imageAltText || title}
        src={image || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 640, 640)}`}
        itemProp='thumbnail'
      />
      {title ? (
        <div className={contentClasses}>
          <h3 className={titleClasses} itemProp='headline'>
            {title}
          </h3>
          {description ? (
            <p className={descriptionClasses} itemProp='accessibilitySummary'>
              {description}
            </p>
          ) : null}
          {relativeTime ? (
            <div className={iconTimeClasses}>
              <FontAwesomeIcon icon={faClock} size='sm' className={iconClasses} />
              <p className={timeClasses}>{relativeTime}</p>
              {author ? (
                <>
                  <span>{` - `}</span>
                  <p
                    className={timeClasses}
                    itemScope
                    itemProp='author'
                    itemType='https://schema.org/Person'
                  >
                    <span itemProp='name'>{author}</span>
                  </p>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
